import dayjs from 'dayjs';

import { formatDisplayDate, formatDisplayDateTime, formatDisplayTime, parseStoredDate, parseStoredTime } from './dateDisplay';
import { describeWeekdays as describeWeekdayRule } from './dateOptions';

/**
 * The date element's BOUNDS and its RANGE pairing — the one module that reads
 * `options.min` / `options.max` / `options.rangeEnd`, resolves them against a
 * form's other values, and says in plain words what they mean.
 *
 * The wire contract (frozen with the API half, docs/plans/
 * card-editor-dates-groups-filters.md Part 2c), flat under `element.options`:
 *
 *   min / max   { kind: 'literal',  value }            value in the element's
 *                                                      STORED shape
 *               { kind: 'relative', value }            'today' · 'today-30d' ·
 *                                                      'now' · 'now+2h'
 *               { kind: 'element',  name, inclusive }  another date element of
 *                                                      the same type on this
 *                                                      card or an ancestor
 *   rangeEnd    '<element name>' on the START element: the two elements read
 *               as one span, and the end is implicitly ≥ the start.
 *   minuteStep  1 | 5 | 15 | 30, for date-time and time.
 *
 * The API resolves a relative bound in UTC with a day of slack (it cannot know
 * the reader's zone); the WEB resolves it exactly, in local time, which is the
 * time the reader typed in. That asymmetry is deliberate: the form is precise
 * about what it will accept, and the server never refuses something the form
 * allowed at the edge of a day.
 *
 * A range is TWO PLAIN ELEMENTS, not a new value shape — which is what keeps
 * sorting, KQL, the primary date, formulas and exports working untouched.
 */

export const DATE_TYPES = ['date', 'date-time', 'time'];

/** Types this module speaks for. (`mode` and `dataType` are the same word.) */
export const isDateType = dataType => DATE_TYPES.includes(dataType);

/** The option keys a date element may carry, for the policy and the pruner. */
export const DATE_OPTION_KEYS = ['min', 'max', 'rangeEnd', 'minuteStep'];

export const MINUTE_STEPS = [1, 5, 15, 30];

export const BOUND_KINDS = ['literal', 'relative', 'element'];

/* ---------------------------------------------------------------------------
 * The relative grammar: an anchor, optionally offset.
 *
 * `today` is local midnight; `now` is this instant, so only a date-time may
 * anchor to it. The units are the platform's own (automation tokens, KQL):
 * d w mo y everywhere, plus h and m under `now` — where `m` can only mean
 * minutes, because the anchor already fixes the scale. Under `today`, months
 * are `mo` and nothing is finer than a day.
 * ------------------------------------------------------------------------- */

const TODAY_UNITS = { d: 'day', w: 'week', mo: 'month', y: 'year' };

const NOW_UNITS = { h: 'hour', m: 'minute', ...TODAY_UNITS };

const UNIT_WORDS = {
	minute: 'minute', hour: 'hour', day: 'day', week: 'week', month: 'month', year: 'year',
};

const RELATIVE_RE = /^(today|now)(?:([+-])(\d{1,4})(mo|[hmdwy]))?$/;

/** The units an anchor offers, as [{ value, label }] for a Select. */
export const relativeUnitsFor = anchor => Object.entries(anchor === 'now' ? NOW_UNITS : TODAY_UNITS)
	.map(([value, unit]) => ({ value, label: `${UNIT_WORDS[unit]}s` }));

/** 'today-30d' -> { anchor: 'today', sign: -1, amount: 30, token: 'd' }, or null. */
export function parseRelativeBound(value) {
	const match = RELATIVE_RE.exec(String(value || '').trim().toLowerCase().replace(/\s+/g, ''));
	if (!match) return null;
	const anchor = match[1];
	if (!match[2]) return { anchor, sign: 1, amount: 0, token: anchor === 'now' ? 'h' : 'd' };
	const units = anchor === 'now' ? NOW_UNITS : TODAY_UNITS;
	if (!units[match[4]]) return null;
	return { anchor, sign: match[2] === '-' ? -1 : 1, amount: Number(match[3]), token: match[4] };
}

/** The pieces back to the stored spelling ('today', 'today-30d', 'now+2h'). */
export function buildRelativeBound({ anchor = 'today', sign = 1, amount = 0, token = 'd' } = {}) {
	const units = anchor === 'now' ? NOW_UNITS : TODAY_UNITS;
	if (!amount || !units[token]) return anchor;
	return `${anchor}${sign < 0 ? '-' : '+'}${Math.abs(Math.trunc(amount))}${token}`;
}

/** A relative bound as the instant it names, or null. */
export function resolveRelativeBound(value, { now } = {}) {
	const parts = parseRelativeBound(value);
	if (!parts) return null;
	const base = now ? dayjs(now) : dayjs();
	if (!base.isValid()) return null;
	const anchored = parts.anchor === 'now' ? base : base.startOf('day');
	const units = parts.anchor === 'now' ? NOW_UNITS : TODAY_UNITS;
	return parts.amount ? anchored.add(parts.sign * parts.amount, units[parts.token]) : anchored;
}

/** A relative bound in words: "today", "30 days before today", "2 hours from now". */
export function describeRelativeBound(value) {
	const parts = parseRelativeBound(value);
	if (!parts) return '';
	if (!parts.amount) return parts.anchor;
	const units = parts.anchor === 'now' ? NOW_UNITS : TODAY_UNITS;
	const word = UNIT_WORDS[units[parts.token]];
	const plural = parts.amount === 1 ? word : `${word}s`;
	if (parts.sign < 0) return `${parts.amount} ${plural} before ${parts.anchor}`;
	return parts.anchor === 'now' ? `${parts.amount} ${plural} from now` : `${parts.amount} ${plural} after today`;
}

/* ---------------------------------------------------------------------------
 * Stored values as comparable instants.
 * ------------------------------------------------------------------------- */

/**
 * A stored value of `mode` as a dayjs, or null. `reference` gives a bare clock
 * its day, so a `time` element's value and its bound are compared on the same
 * one (otherwise "now" would drift between the two reads).
 */
export function momentOfStored(mode, value, reference) {
	if (value === null || value === undefined || value === '') return null;
	if (mode === 'time') return parseStoredTime(value, reference);
	if (mode === 'date') return parseStoredDate(value);
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed : null;
}

/* ---------------------------------------------------------------------------
 * What a type can READ
 *
 * The stored shapes again, this time as a yes/no about a bound rather than a
 * value — because an element's data type can CHANGE, and a bound the new type
 * cannot read is dead configuration the API refuses the whole card over (400
 * invalid_options). Deliberately stricter than `momentOfStored`, which is
 * permissive on purpose so a legacy value still renders: 'HH:mm' is a time
 * and nothing else, and a date string is not a wall clock however happily
 * dayjs parses it.
 * ------------------------------------------------------------------------- */

const ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_STAMP_RE = /^\d{4}-\d{2}-\d{2}T/;
const BARE_CLOCK_RE = /^\d{1,2}:\d{2}(:\d{2})?$/;

/** Could `mode` have STORED this literal? (The API's own `comparable`.) */
export function literalFitsType(mode, value) {
	const text = typeof value === 'string' ? value.trim() : '';
	if (!text) return false;
	if (mode === 'time') return BARE_CLOCK_RE.test(text);
	if (mode !== 'date' && mode !== 'date-time') return false;
	return (ISO_DAY_RE.test(text) || ISO_STAMP_RE.test(text)) && dayjs(text).isValid();
}

/**
 * Can `mode` read this whole bound? A `time` is offered no relative bound (a
 * wall clock has no "today"), only a `date-time` has a `now` to anchor to, and
 * a literal has to be a value the element could hold. An `element` bound names
 * a sibling whose type this module cannot see from here — the pruner settles
 * that one from the type the element is LEAVING.
 */
export function boundFitsType(bound, mode) {
	if (!bound || typeof bound !== 'object' || !isDateType(mode)) return false;
	if (bound.kind === 'literal') return literalFitsType(mode, bound.value);
	if (bound.kind === 'relative') {
		const parts = parseRelativeBound(bound.value);
		if (!parts || mode === 'time') return false;
		return parts.anchor !== 'now' || mode === 'date-time';
	}
	return bound.kind === 'element' && !!bound.name;
}

/** A bound's instant in the words the reader's preferences print. */
export function formatBoundValue(mode, value) {
	if (mode === 'time') return formatDisplayTime(value);
	if (mode === 'date-time') return formatDisplayDateTime(value);
	return formatDisplayDate(value);
}

const labelOfElement = element => element?.label?.singular || element?.name || 'another field';

const elementByName = (elements, name) => (elements || []).find(el => el?.name === name) || null;

/**
 * ONE bound as `{ moment, inclusive, label }`, or null when it does not
 * resolve — an element bound whose other field is empty is simply not a bound
 * yet, which is why a half-filled range never refuses a save.
 *
 * `ctx`: { mode, elements, values, now, side }. `side` ('min' | 'max') is
 * needed because a `today` anchor names a DAY, and which end of that day it
 * means depends on which end of the span it is.
 */
export function resolveBound(bound, ctx = {}) {
	if (!bound || typeof bound !== 'object') return null;
	const { mode = 'date', elements = [], values = {}, now, side = 'min' } = ctx;
	if (bound.kind === 'literal') {
		const moment = momentOfStored(mode, bound.value, now);
		return moment ? { moment, inclusive: true, label: formatBoundValue(mode, bound.value) } : null;
	}
	if (bound.kind === 'relative') {
		const moment = resolveRelativeBound(bound.value, { now });
		if (!moment) return null;
		// `today` (and `today±Nd`) names a whole DAY. As a floor that is the
		// day's first moment, which is what resolveRelativeBound already
		// returns; as a CEILING on a date & time it has to be the day's LAST
		// moment, or "No future dates" (`max: today`) would refuse every hour
		// of today and accept only the stroke of midnight. A `now` anchor
		// names an instant and keeps it; a `date` element has no clock, so
		// either end of the day is the same day.
		const anchoredToDay = parseRelativeBound(bound.value)?.anchor === 'today';
		const at = (mode !== 'date' && side === 'max' && anchoredToDay)
			? moment.endOf('day')
			: (mode === 'date' ? moment.startOf('day') : moment);
		return {
			moment: at,
			inclusive: true,
			label: describeRelativeBound(bound.value),
		};
	}
	if (bound.kind === 'element') {
		const other = elementByName(elements, bound.name);
		const moment = momentOfStored(mode, values?.[bound.name], now);
		if (!moment) return null;
		return {
			moment,
			inclusive: bound.inclusive !== false,
			label: labelOfElement(other) || bound.name,
		};
	}
	return null;
}

/** The tighter of two floors (or ceilings): `keepLater` picks the later one. */
const tighter = (a, b, keepLater) => {
	if (!a) return b;
	if (!b) return a;
	if (a.moment.isSame(b.moment)) return a.inclusive ? b : a;
	const aWins = keepLater ? a.moment.isAfter(b.moment) : a.moment.isBefore(b.moment);
	return aWins ? a : b;
};

/**
 * The floor and ceiling an element's value must sit between RIGHT NOW: its
 * own `min` / `max`, and — when another element names it as its range end —
 * the implicit "not before the start". The tighter of the two wins, so the
 * calendar greys exactly what the validator refuses.
 *
 * `ctx`: { elements, values, now }. `elements` is every element the form can
 * see (the card and its ancestors), which is also where an element bound and
 * a range partner are looked up.
 */
export function resolveBounds(element, ctx = {}) {
	const mode = element?.dataType;
	if (!isDateType(mode)) return { min: null, max: null };
	const options = element?.options || {};
	const inner = { ...ctx, mode };
	const floor = { ...inner, side: 'min' };
	let min = resolveBound(options.min, floor);
	const max = resolveBound(options.max, { ...inner, side: 'max' });

	// The other half of a range: its start is a floor nobody had to configure.
	const start = rangeStartFor(element?.name, ctx.elements);
	if (start) {
		min = tighter(min, resolveBound({ kind: 'element', name: start.name, inclusive: true }, floor), true);
	}
	return { min, max };
}

/** Does `value` sit inside `bound`? `side` is 'min' or 'max'. */
export function satisfiesBound(mode, value, bound, side, { now } = {}) {
	if (!bound) return true;
	const moment = momentOfStored(mode, value, now);
	if (!moment) return true;
	// A `date` is a day: compare days, never the midnight instants under them.
	const unit = mode === 'date' ? 'day' : 'minute';
	const diff = moment.startOf(unit).diff(bound.moment.startOf(unit));
	if (side === 'min') return bound.inclusive ? diff >= 0 : diff > 0;
	return bound.inclusive ? diff <= 0 : diff < 0;
}

/** "on or after Start" / "after Sep 1, 2026" — the tail of every bound message. */
export function describeBound(bound, side) {
	if (!bound) return '';
	if (side === 'min') return `${bound.inclusive ? 'on or after' : 'after'} ${bound.label}`;
	return `${bound.inclusive ? 'on or before' : 'before'} ${bound.label}`;
}

/**
 * The same tail for the CARD EDITOR, which has no values to resolve against —
 * an element bound there is "on or after Start", not a date. This is the one
 * that reads an option; `describeBound` reads a resolved one.
 */
export function describeBoundOption(bound, side, { mode = 'date', elements = [] } = {}) {
	if (!bound || typeof bound !== 'object') return '';
	const inclusive = bound.kind === 'element' ? bound.inclusive !== false : true;
	const word = side === 'min'
		? (inclusive ? 'on or after' : 'after')
		: (inclusive ? 'on or before' : 'before');
	if (bound.kind === 'literal') {
		const text = formatBoundValue(mode, bound.value);
		return text ? `${word} ${text}` : '';
	}
	if (bound.kind === 'relative') {
		const text = describeRelativeBound(bound.value);
		return text ? `${word} ${text}` : '';
	}
	if (bound.kind === 'element') {
		const other = elementByName(elements, bound.name);
		return bound.name ? `${word} ${other ? labelOfElement(other) : bound.name}` : '';
	}
	return '';
}

/**
 * The API's own refusal of a written value — 400 `range`
 * `{ element, bound: 'min'|'max', against?: '<element>', limit?: <literal> }`
 * — in the words the field shows for the same rule. The web checks first and
 * almost always catches it, so a refusal that reaches here is the edge the
 * server saw differently (it resolves a relative bound in UTC with a day of
 * slack): it lands on the field, never as a toast the reader has to carry
 * back to a form they cannot see any more.
 */
export function describeRangeRefusal(refusal, { elements = [] } = {}) {
	const element = elementByName(elements, refusal?.element);
	const label = element ? labelOfElement(element) : (refusal?.element || 'This field');
	// The WEEKDAY rule arrives as a third `bound`, carrying the allowed set as
	// its `limit`. It is not a floor or a ceiling, so it gets its own
	// sentence — the same one the form's own rule shows, in the same words.
	if (refusal?.bound === 'weekdays') {
		const days = describeWeekdayRule(refusal?.limit ?? element?.options?.weekdays);
		return days ? `${label} must fall on ${days}.` : `${label} is not on a day this field allows.`;
	}
	// Strict or not. The refusal does not carry `inclusive`, so the element's
	// own option is read for it: a strict bound reported as "on or after"
	// would send the reader back with the very value the rule refuses. An
	// implied range floor has no option and is inclusive, which is the
	// default here.
	const option = element?.options?.[refusal?.bound === 'max' ? 'max' : 'min'];
	const inclusive = !(option && option.kind === 'element'
		&& option.name === refusal?.against && option.inclusive === false);
	const word = refusal?.bound === 'max'
		? (inclusive ? 'on or before' : 'before')
		: (inclusive ? 'on or after' : 'after');
	if (refusal?.against) {
		const other = elementByName(elements, refusal.against);
		return `${label} must be ${word} ${other ? labelOfElement(other) : refusal.against}.`;
	}
	if (refusal?.limit !== undefined && refusal?.limit !== null && refusal.limit !== '') {
		const text = formatBoundValue(element?.dataType || 'date', refusal.limit);
		if (text) return `${label} must be ${word} ${text}.`;
	}
	return `${label} is outside the dates this field allows.`;
}

/* ---------------------------------------------------------------------------
 * Range pairing
 * ------------------------------------------------------------------------- */

/** The end element a start element names, when the link is a legal one. */
export function rangeEndFor(element, elements) {
	const name = element?.options?.rangeEnd;
	if (!name || typeof name !== 'string' || name === element?.name) return null;
	const end = elementByName(elements, name);
	if (!end || end.dataType !== element?.dataType) return null;
	// No chains and no cycles: an end element may not start a range of its own.
	if (end?.options?.rangeEnd) return null;
	return end;
}

/**
 * The sibling elements whose Earliest / Latest NAMES this one — the other
 * direction of an element bound, which nothing else can see from the element
 * itself. A bound compares ONE date against one date, so an element somebody
 * bounds against may not become a list (utils/dateOptions).
 */
export function boundReferrers(name, elements) {
	if (!name) return [];
	return (elements || []).filter(el => el?.name !== name && ['min', 'max'].some(side => {
		const bound = el?.options?.[side];
		return !!bound && typeof bound === 'object' && bound.kind === 'element' && bound.name === name;
	}));
}

/** The start element that names `name` as its end, if any (first wins). */
export function rangeStartFor(name, elements) {
	if (!name) return null;
	return (elements || []).find(el => el?.name !== name && rangeEndFor(el, elements)?.name === name) || null;
}

/**
 * Entries paired into the slots a form or a read view renders: a start
 * element's slot carries its end, and the end element LEAVES ITS OWN SLOT
 * wherever in the order it sat — a span is one thing, and reading it as one
 * is the whole point of the pairing.
 *
 * Returns [{ entry, end }] over the caller's own records (`getElement` pulls
 * the element out of one, as `groupElementsIntoPanels` does).
 */
export function pairRangeEntries(entries, { getElement = entry => entry } = {}) {
	const elements = (entries || []).map(getElement).filter(Boolean);
	const claimed = new Set();
	const slots = [];
	(entries || []).forEach(entry => {
		const element = getElement(entry);
		if (!element?.name || claimed.has(element.name)) return;
		const end = rangeEndFor(element, elements);
		// Only the FIRST start to claim an end gets it; a second keeps its own
		// plain field rather than showing the same value twice.
		const endEntry = end && !claimed.has(end.name)
			? (entries || []).find(other => getElement(other)?.name === end.name)
			: null;
		if (endEntry) claimed.add(end.name);
		slots.push({ entry, end: endEntry || null });
	});
	return slots.filter(slot => !claimed.has(getElement(slot.entry)?.name) || slot.end);
}

/** The elements that are somebody's range end — the ones with no row of their own. */
export function rangeEndNames(elements) {
	const names = new Set();
	(elements || []).forEach(el => {
		const end = rangeEndFor(el, elements);
		if (end) names.add(end.name);
	});
	return names;
}

/** 1 | 5 | 15 | 30, or undefined for anything else. */
export const minuteStepOf = options => (
	MINUTE_STEPS.includes(options?.minuteStep) ? options.minuteStep : undefined
);
